// Pure formatting helpers for the Deals kanban surface.
// Extracted from `DealsKanbanPage.tsx` so the threshold logic
// (KRW abbreviations: 만 / 억) and the DnD column visual-state
// machine can be unit-tested without rendering the kanban.
//
// The wire format for `expectedRevenue` is the full KRW value
// as a numeric string (BigInt over the wire per
// `decisions.md §C-6`). The kanban card is space-constrained,
// so we abbreviate:
//   ≥ 100,000,000  →  N.N억   (units: 1억 = 100M KRW)
//   ≥ 10,000       →  N만     (units: 1만 = 10K KRW)
//   else           →  N.toLocaleString()
//
// Non-finite inputs (e.g. malformed wire payload) round-trip
// the original string verbatim — better to render the raw value
// than to display "NaN억" on the card.

import type { CSSProperties } from 'react';

export function formatKRW(numericString: string): string {
  const n = Number(numericString);
  if (!Number.isFinite(n)) return numericString;
  if (n >= 1e8) return `${(n / 1e8).toFixed(1)}억`;
  if (n >= 1e4) return `${(n / 1e4).toFixed(0)}만`;
  return n.toLocaleString();
}

// ----- DnD column visual state ------------------------------

const COLUMN_BASE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 'var(--radius-md)',
  background: 'var(--bg-sunken)',
  border: '1px solid var(--border-default)',
  minHeight: 0,
};

/**
 * 3-state visual machine for kanban columns during drag-and-drop:
 *
 *   neutral        — base style; not over OR no drag in flight.
 *   drop-allowed   — operator hovers over a valid drop target;
 *                    blue tint + brand border.
 *   drop-forbidden — operator hovers over an invalid drop target
 *                    (SM-table denies the transition); danger
 *                    tint + danger border.
 *
 * `dropAllowed` and `dropForbidden` are mutually exclusive in
 * practice but the helper handles both being false (= neutral)
 * and is defensively ordered so `dropAllowed` wins if both
 * happen to be true.
 */
export function columnStyle(
  isOver: boolean,
  dropAllowed: boolean,
  dropForbidden: boolean,
): CSSProperties {
  if (isOver && dropAllowed) {
    return {
      ...COLUMN_BASE,
      background: 'var(--blue-50)',
      border: '1px solid var(--blue-500)',
    };
  }
  if (isOver && dropForbidden) {
    return {
      ...COLUMN_BASE,
      background: 'var(--bg-danger-subtle)',
      border: '1px solid var(--fg-danger)',
    };
  }
  return COLUMN_BASE;
}

// Re-exported for the page-level DealsKanbanPage's other
// styles to compose against.
export const KANBAN_COLUMN_BASE: CSSProperties = COLUMN_BASE;
